import {BlockerContext, DataStatus, NavigateWithoutBlocker} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  CreateQrCodeSettingsRequestBody,
  QrCodeSettingsResponseBody,
  useCreateQrCodeSettingsMutation,
  useGetQrCodeSettingsQuery,
  useUpdateQrCodeSettingsMutation,
} from '@dms/api/finance';
import {settingsRoutes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {QrCodeSettingsDetailForm} from './QrCodeSettingsDetailForm';

export function QrCodeSettingsDefinitionDetail() {
  const navigate = useNavigate();

  const {id} = useParams();
  const isCreating = isNil(id);
  const {
    data: settingsDetail,
    isLoading,
    isError,
  } = useGetQrCodeSettingsQuery({id: id ?? ''}, {skip: isCreating});

  const [updateQrCodeSettings] = useUpdateQrCodeSettingsMutation();
  const [createQrCodeSettings] = useCreateQrCodeSettingsMutation();

  const handleSubmit = async (
    values: Omit<QrCodeSettingsResponseBody, 'id'>,
    blockerContext?: BlockerContext
  ) => {
    if (!isCreating) {
      if (!settingsDetail) {
        return;
      }

      await updateQrCodeSettings({
        id: settingsDetail.id,
        updateQrCodeSettingsRequestBody: values,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.qrCodeSettings);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else {
      await createQrCodeSettings({
        createQrCodeSettingsRequestBody: values as CreateQrCodeSettingsRequestBody,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.qrCodeSettings);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  const handleCancel = (navigateWithoutBlocker: NavigateWithoutBlocker) => {
    navigateWithoutBlocker(settingsRoutes.qrCodeSettings);
  };

  const testIdBase = isCreating
    ? testIds.settings.qrCodeSettingsAdd
    : testIds.settings.qrCodeSettingsEdit;

  return (
    <DataStatus isError={isError} isLoading={isLoading} data-testid={testIdBase('dataStatus')}>
      <QrCodeSettingsDetailForm
        defaultValues={
          settingsDetail
            ? {
                branchId: settingsDetail.branchId,
                bankTransfer: settingsDetail.bankTransfer,
                cash: settingsDetail.cash,
                card: settingsDetail.card,
                offset: settingsDetail.offset,
                freeOfCharge: settingsDetail.freeOfCharge,
                cashOnDelivery: settingsDetail.cashOnDelivery,
                isSystem: settingsDetail.isSystem,
              }
            : {
                branchId: null,
                bankTransfer: false,
                cash: false,
                card: false,
                offset: false,
                freeOfCharge: false,
                cashOnDelivery: false,
                isSystem: false,
              }
        }
        isEdit={!isCreating}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        data-testid={testIdBase('definition')}
      />
    </DataStatus>
  );
}
